import React, { useEffect, useState } from 'react';
import { fetchInvoiceById } from '../lib/orderInvoice';
import { startTelrPayment, paymentStartErrorText } from '../lib/telrPay';
import { fetchPaymentQuote, formatSar, sarFixedNote } from '../lib/paymentQuote';
import { sb } from '../supabase';
import { translateCardFields } from '../lib/cardTranslation';

// Compact invoice card inside the chat (the [invoice:ID] message), in Maabar's
// identity (dark header + wordmark). Role-aware money: the buyer sees the total
// they pay (incl. the visible 5% fee) + Pay buttons; the supplier sees only THEIR
// receivable (the goods value) — the fee is on the buyer, never shown as theirs.
// A «حصريات معبر» invoice (SAR, maabar_fee_amount 0) says it has no fee, and its
// shipping line (kind 'shipping') is named as shipping, not counted as an item.
const LOGO = `${(typeof window !== 'undefined' ? window.location.origin : '')}/email-logo.png`;
const money = (n, c) => `${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${c || 'SAR'}`;

// Was a flat `isAr ? 'عربي' : 'English'` ternary, which sent a Chinese reader to
// ENGLISH — a different wrong answer from the purchase-request card next to it,
// which fell back to Arabic. Both are now real three-language tables.
const T = {
  ar: {
    invoice: 'فاتورة', feeBuyer: 'شامل رسوم مَعبر 5%', receivable: 'مستحقّك · قيمة البضاعة',
    deposit: 'ادفع العربون (30%)', full: 'ادفع كامل', paid: '✓ مدفوعة', awaiting: 'بانتظار دفع المشتري',
    more: (n) => `+${n} صنف`, noFee: 'السعر النهائي — بدون رسوم', withShip: 'شامل الشحن',
    notOnline: 'الدفع الإلكتروني غير متاح لهذه الفاتورة بعد — تواصل مع دعم معبر.',
  },
  en: {
    invoice: 'Invoice', feeBuyer: 'incl. 5% Maabar fee', receivable: 'Your receivable · goods',
    deposit: 'Pay deposit (30%)', full: 'Pay in full', paid: '✓ Paid', awaiting: 'Awaiting buyer payment',
    more: (n) => `+${n} more`, noFee: 'Final price — no fee', withShip: 'incl. shipping',
    notOnline: "This invoice can't be paid online yet — contact Maabar support.",
  },
  zh: {
    invoice: '发票', feeBuyer: '含 Maabar 5% 服务费', receivable: '您的应收 · 货值',
    deposit: '支付定金（30%）', full: '全额支付', paid: '✓ 已支付', awaiting: '等待买家付款',
    more: (n) => `另 +${n} 项`, noFee: '最终价格 — 无服务费', withShip: '含运费',
    notOnline: '此发票暂不支持在线支付 — 请联系 Maabar 客服。',
  },
};

export default function ChatInvoiceCard({ invoiceId, messageId, myId, lang = 'ar' }) {
  const [inv, setInv] = useState(null);
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState(null);   // a payment that could not start — shown inline
  const [tx, setTx] = useState({});
  const [quote, setQuote] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => { const d = await fetchInvoiceById(invoiceId); if (!cancelled) setInv(d); })();
    return () => { cancelled = true; };
  }, [invoiceId]);

  // The buyer's figure comes from payment_quote() — the same call telr-create
  // charges from — at the rate locked when the invoice was issued. Only the
  // buyer may read it (and only the buyer needs it): the supplier's card shows
  // their receivable in the invoice's own currency, untouched.
  useEffect(() => {
    if (!inv || inv.buyer_id !== myId) return undefined;
    let off = false;
    (async () => { const q = await fetchPaymentQuote(sb, { invoiceId }); if (!off) setQuote(q); })();
    return () => { off = true; };
  }, [inv, myId, invoiceId]);

  // The line description is typed by the supplier issuing the invoice, so it is
  // free text like the purchase-request fields. Quantities and money are not
  // translated — they are identical in every language.
  useEffect(() => {
    if (!inv) return undefined;
    let off = false;
    const first = Array.isArray(inv.line_items) && inv.line_items[0];
    if (!first?.desc) return undefined;
    (async () => {
      const out = await translateCardFields({ messageId, readerLang: lang, fields: { desc: first.desc } });
      if (!off) setTx(out);
    })();
    return () => { off = true; };
  }, [inv, messageId, lang]);

  const t = T[lang] || T.ar;

  // Telr hosted page; on success the browser leaves. On failure the card says
  // so in the trader's language (zh readers get English) — not a raw alert().
  const pay = (stage) => {
    setPaying(true);
    setPayError(null);
    startTelrPayment(invoiceId, stage, 'invoice').catch((e) => {
      console.error('[ChatInvoiceCard] could not start the payment:', e?.status || '', e?.message || e);
      setPayError(paymentStartErrorText(e, lang));
      setPaying(false);
    });
  };

  if (!inv) return <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>…</div>;
  const items = Array.isArray(inv.line_items) ? inv.line_items : [];
  const goodsItems = items.filter((it) => it?.kind !== 'shipping');
  const hasShip = goodsItems.length < items.length;
  const line = goodsItems[0] || items[0];
  const extra = Math.max(0, goodsItems.length - 1);
  const noFee = inv.maabar_fee_amount != null && Number(inv.maabar_fee_amount) === 0;
  const isPaid = inv.status === 'paid';
  const isBuyer = inv.buyer_id === myId;
  const invCur = String(inv.currency || 'SAR').toUpperCase();
  const goods = inv.goods_subtotal != null ? Number(inv.goods_subtotal) : Number(inv.total) / 1.05;
  // Buyer: SAR only, from payment_quote() — the rate and the supplier's own
  // figure are internal (owner, 2026-09-29). Supplier: their receivable in the
  // invoice's currency, unchanged. A buyer whose quote is not payable (CNY, or
  // the call failed) sees no amount and no Pay buttons — never the USD/CNY.
  const payable = isBuyer && Boolean(quote?.payable);
  const primary = isBuyer
    ? (payable ? `${formatSar(quote.total_sar)} SAR` : '—')
    : money(goods, invCur);
  const usdRef = payable ? sarFixedNote('invoice', lang) : '';

  return (
    <div style={{ border: '1px solid rgba(154,123,79,0.35)', borderRadius: 12, overflow: 'hidden', maxWidth: 300, background: '#F7F4EE', boxShadow: '0 6px 20px -8px rgba(20,18,16,0.4)' }}>
      <div style={{ background: '#0E0D0C', color: '#F3EFE7', padding: '9px 13px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid #9A7B4F' }}>
        <img src={LOGO} alt="MAABAR" style={{ height: 16, filter: 'brightness(0) invert(1)' }} />
        <span style={{ fontSize: 10.5, letterSpacing: '0.04em', color: 'rgba(243,239,231,0.75)', fontVariantNumeric: 'tabular-nums' }}>{inv.invoice_number}</span>
      </div>
      <div style={{ padding: '11px 13px', color: '#141210' }}>
        <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#9A7B4F' }}>{t.invoice}</div>
        {line && (
          <div style={{ fontSize: 13, fontWeight: 600, color: '#141210', margin: '5px 0 2px' }}>
            {tx.desc || line.desc} × {Number(line.qty).toLocaleString('en-US')}
            {extra > 0 && <span style={{ fontSize: 11, color: '#6B655B', fontWeight: 400 }}> {t.more(extra)}</span>}
          </div>
        )}
        <div style={{ fontSize: 17, fontWeight: 700, color: '#141210', fontFamily: "'Palatino Linotype',Georgia,serif", fontVariantNumeric: 'tabular-nums' }}>{primary}</div>
        {usdRef && <div style={{ fontSize: 11, color: '#8F887C', fontVariantNumeric: 'tabular-nums' }}>{usdRef}</div>}
        <div style={{ fontSize: 10.5, color: '#6B655B', marginBottom: isBuyer && !isPaid ? 11 : 0 }}>{isBuyer ? [noFee ? t.noFee : t.feeBuyer, hasShip ? t.withShip : ''].filter(Boolean).join(' · ') : t.receivable}</div>
        {isPaid ? (
          <div style={{ fontSize: 12, color: '#2F5D3A', fontWeight: 700, marginTop: 7 }}>{t.paid}</div>
        ) : payable ? (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button disabled={paying} onClick={() => pay('deposit')}
              style={{ padding: '7px 11px', fontSize: 11.5, fontWeight: 600, border: '1px solid #141210', borderRadius: 8, background: 'none', color: '#141210', cursor: 'pointer' }}>{t.deposit}</button>
            <button disabled={paying} onClick={() => pay('full')}
              style={{ padding: '7px 13px', fontSize: 11.5, fontWeight: 600, border: 'none', borderRadius: 8, background: '#141210', color: '#F7F4EE', cursor: 'pointer' }}>{t.full}</button>
            {payError && (
              <div role="alert" style={{ flexBasis: '100%', marginTop: 4, padding: '8px 10px', borderRadius: 8, background: 'rgba(138,59,46,0.08)', border: '1px solid rgba(138,59,46,0.35)' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#8A3B2E' }}>{payError.title}</div>
                <div style={{ fontSize: 11.5, color: '#141210', lineHeight: 1.6 }}>{payError.body}</div>
                {payError.detail && <div dir="ltr" style={{ fontSize: 10.5, color: '#6B655B', marginTop: 3 }}>{payError.detail}</div>}
              </div>
            )}
          </div>
        ) : isBuyer ? (
          <div style={{ fontSize: 11.5, color: '#6B655B', marginTop: 7 }}>{quote ? t.notOnline : '…'}</div>
        ) : (
          <div style={{ fontSize: 11.5, color: '#6B655B', marginTop: 7 }}>{t.awaiting}</div>
        )}
      </div>
    </div>
  );
}
